// Serviço do domínio appointments — agenda pessoal do médico.
import { createClient } from '@/lib/supabase/client'
import type { Consulta, SituacaoConsulta, TipoConsulta } from '@/lib/types'
import { SITUACAO_CONSULTA_LABEL } from '@/lib/types'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'

export interface ConsultaComPaciente extends Consulta {
  paciente_nome: string
}

const CAMPOS =
  'id, workspace_id, paciente_id, clinica_id, data, hora_inicio, hora_fim, tipo, motivo, observacoes, situacao, status_prontidao, origem, pacientes(nome)'

function mapRow(row: Record<string, unknown>): ConsultaComPaciente {
  const raw = row.pacientes
  const pac = (Array.isArray(raw) ? raw[0] : raw) as { nome: string } | null
  const { pacientes: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as Consulta), paciente_nome: pac?.nome ?? '—' }
}

export async function listConsultas(params: {
  workspaceId: string
  de: string   // 'YYYY-MM-DD'
  ate: string  // 'YYYY-MM-DD'
  clinicaId?: string | 'todas'
}): Promise<ConsultaComPaciente[]> {
  const supabase = createClient()
  let q = supabase
    .from('consultas')
    .select(CAMPOS)
    .eq('workspace_id', params.workspaceId)
    .gte('data', params.de)
    .lte('data', params.ate)
    .order('data')
    .order('hora_inicio')
    .limit(1000)
  if (params.clinicaId && params.clinicaId !== 'todas') q = q.eq('clinica_id', params.clinicaId)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []).map(r => mapRow(r as Record<string, unknown>))
}

export async function listConsultasDoPaciente(workspaceId: string, pacienteId: string): Promise<ConsultaComPaciente[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('consultas')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .eq('paciente_id', pacienteId)
    .order('data', { ascending: false })
    .order('hora_inicio', { ascending: false })
    .limit(100)
  if (error) throw error
  return (data ?? []).map(r => mapRow(r as Record<string, unknown>))
}

export async function getConsulta(workspaceId: string, id: string): Promise<ConsultaComPaciente | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('consultas')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data ? mapRow(data as Record<string, unknown>) : null
}

export interface DadosConsulta {
  paciente_id: string
  clinica_id?: string | null
  data: string
  hora_inicio: string
  hora_fim?: string | null
  tipo: TipoConsulta
  motivo?: string | null
  observacoes?: string | null
}

export async function criarConsulta(params: {
  workspaceId: string
  dados: DadosConsulta
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('consultas')
    .insert({ workspace_id: params.workspaceId, ...params.dados })
    .select('id')
    .single()
  if (error) throw error
  const consultaId = (data as { id: string }).id

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.dados.paciente_id,
    tipo: 'consulta_criada',
    titulo: `Consulta agendada — ${params.dados.data.split('-').reverse().join('/')} ${params.dados.hora_inicio.slice(0, 5)}`,
    descricao: params.dados.motivo ?? null,
    consultaId,
    eventoDominio: 'appointment.created',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'consulta_criada',
    entidade: 'consultas',
    registroId: consultaId,
  })
  return consultaId
}

export async function atualizarConsulta(params: {
  workspaceId: string
  id: string
  pacienteId: string
  dados: Partial<DadosConsulta>
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('consultas')
    .update(params.dados)
    .eq('id', params.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'consulta_atualizada',
    entidade: 'consultas',
    registroId: params.id,
    dados: params.dados as Record<string, unknown>,
  })
}

export async function mudarSituacaoConsulta(params: {
  workspaceId: string
  id: string
  pacienteId: string
  situacao: SituacaoConsulta
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('consultas')
    .update({ situacao: params.situacao })
    .eq('id', params.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    tipo: 'consulta_situacao',
    titulo: `Consulta ${SITUACAO_CONSULTA_LABEL[params.situacao].toLowerCase()}`,
    consultaId: params.id,
    eventoDominio: 'appointment.updated',
    dados: { situacao: params.situacao },
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'consulta_situacao',
    entidade: 'consultas',
    registroId: params.id,
    dados: { situacao: params.situacao },
  })
}
